import { useEffect, useState } from "react";
import { AlertTriangle, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";

interface FloatingChecklistHeaderProps {
  checklistName: string;
  completedCount: number;
  totalTasks: number;
  timerStartedAt?: string | null;
  isVisible: boolean;
  isSaving?: boolean;
  /** Optional one-line turnover step (entry / clean / inventory). */
  flowSummary?: string | null;
  onReportIssue?: () => void;
}

const FloatingChecklistHeader = ({
  checklistName,
  completedCount,
  totalTasks,
  timerStartedAt,
  isVisible,
  isSaving = false,
  flowSummary = null,
  onReportIssue,
}: FloatingChecklistHeaderProps) => {
  const { tDynamic, isRtl } = useTranslation();
  const [elapsedTime, setElapsedTime] = useState<string>("00:00");

  // Timer logic
  useEffect(() => {
    if (!timerStartedAt) {
      setElapsedTime("00:00");
      return;
    }

    const updateElapsed = () => {
      const start = new Date(timerStartedAt).getTime();
      const now = Date.now();
      const diff = Math.floor((now - start) / 1000);
      const hours = Math.floor(diff / 3600);
      const mins = Math.floor((diff % 3600) / 60);
      const secs = diff % 60;

      if (hours > 0) {
        setElapsedTime(
          `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
        );
      } else {
        setElapsedTime(`${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [timerStartedAt]);

  const percentage = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;

  return (
    <div
      className={`cleaner-turnover-float-header fixed top-0 left-0 right-0 z-50 border-b border-[color:var(--pv-line,hsl(var(--border)))] bg-background/92 backdrop-blur-md transition-all duration-200 ease-out ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0 pointer-events-none"
      }`}
      style={{ paddingTop: "max(8px, env(safe-area-inset-top))" }}
    >
      <div className="max-w-2xl mx-auto px-1">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" dir={isRtl ? "rtl" : "ltr"}>
          <div className="flex justify-center gap-2 sm:justify-start sm:order-2">
            {onReportIssue ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1 border-amber-300/80 bg-amber-50/80 px-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                onClick={onReportIssue}
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {tDynamic("Report")}
              </Button>
            ) : null}
            {timerStartedAt ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-[color:color-mix(in_srgb,var(--pv-teal)_32%,var(--pv-line,hsl(var(--border))))] bg-[color:var(--pv-teal-soft,rgba(15,168,154,0.12))] px-3 py-1.5 shadow-sm">
                <Clock className="h-3.5 w-3.5 shrink-0 text-[color:var(--pv-teal,#0fa89a)]" aria-hidden />
                <span className="text-[10px] font-bold uppercase tracking-wide text-[color:var(--pv-teal-press,#0f766e)]">
                  {tDynamic("Turnover progress")}
                </span>
                <span className="text-xs font-mono font-semibold tabular-nums tracking-tight text-[color:var(--pv-teal-press,#0f766e)]">
                  {elapsedTime}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex-1 min-w-0 sm:order-1">
            <p className="text-sm font-semibold truncate text-foreground">{checklistName}</p>
            {flowSummary ? (
              <p className="text-[10px] sm:text-xs truncate mt-0.5 text-muted-foreground">{flowSummary}</p>
            ) : null}
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {completedCount} / {totalTasks} {tDynamic("tasks")} ({percentage}%)
              </p>
              {isSaving && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {tDynamic("Saving...")}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted/80">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${percentage === 100 ? "bg-green-500" : ""}`}
            style={{
              width: `${percentage}%`,
              ...(percentage === 100 ? {} : { backgroundColor: "var(--pv-teal, #0fa89a)" }),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FloatingChecklistHeader;
